import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { BOOK_NAMES, NT_BOOKS, OT_BOOKS } from '../utils/bibleBooks';
import { BIBLE_VERSE_COUNTS } from '../bible/verseCounts';
import { OutlineTree, renderOutlineHeader } from '../bible/OutlineTree';
import { inferEndRefs, buildOutlineTree, buildOutlinesByVerse } from '../bible/outline';
import { PopupContext, RefContent } from '../bible/VerseWithMarkers';
import { useRefPopups, RefPopupLayer } from '../bible/RefPopups';
import { parseStoredVerses, parseStoredHeadings, chaptersForBook } from '../bible/storedText';
import { getOutlineCache, setOutlineCache, getBookIntroCache, getChapterCache } from '../bible/cache';
import { fetchChapterData } from '../bible/chapterData';
import { fetchBookIntro, OUTLINE_COLS } from '../bible/bookIntro';
import { useUser } from '../context/UserContext';
import { pick, bibleText, annotationsText } from '../i18n';
import { AnnotatedVerse } from '../annotations/AnnotatedVerse';
import { AnnotationLayer } from '../annotations/AnnotationLayer';
import { compareVerses } from '../annotations/anchors';
import { timingLangFor, rowIs, hasVerse, verseStart, verseAt } from '../audio/timings';
import { useTimingRows, playAt } from '../audio/useTimings';
import { useFollowVerse } from '../audio/useFollowVerse';
import FollowAudioPill from '../components/FollowAudioPill';
import { usePageTurn, TurnPage } from '../pageturn/usePageTurn';
import { stepPage, compareChapters } from '../bible/chapterSequence';
import { ChapterPreview, OutlinePreview } from '../bible/ChapterPreview';
import { ChapterScrubber } from '../bible/ChapterScrubber';

// The bars pinned above the chapter, which the playing verse is kept clear of.
const FOLLOW_HEADERS = ['.navbar', '.bible-sticky-toolbar'];

// How far down the window the sticky bars reach, so a turning page is drawn
// from just below them.
function stickyBottom() {
  const bar = document.querySelector('.bible-sticky-toolbar') || document.querySelector('.navbar');
  return bar ? bar.getBoundingClientRect().bottom : 0;
}

// What a verse block says about itself, for lighting and following the verse
// being read (useFollowVerse finds blocks by these, in both columns at once).
const verseAttrs = (book, chapter, verse, lang) =>
  ({ 'data-book': book, 'data-chapter': chapter, 'data-verse': verse, 'data-lang': lang });

const VERSE_NUM_STYLE = { fontSize: '11px', color: 'var(--text-muted)', minWidth: '22px', paddingTop: '4px', fontWeight: 600, flexShrink: 0 };

// A verse number. When the chapter's recording has verse timings it is also a
// button that plays the recording from this verse, marked by a small ▶ (faint
// on touch screens, shown on hover with a mouse; see .bible-verse-play).
function VerseNumber({ verse, onPlay, label }) {
  if (!onPlay) return <span style={VERSE_NUM_STYLE}>{verse}</span>;
  return (
    <button type="button" className="bible-verse-play" style={VERSE_NUM_STYLE}
      onClick={onPlay} title={label} aria-label={label}>
      {verse}<span className="verse-play-icon" aria-hidden="true">▶</span>
    </button>
  );
}

// ── Memoized sidebar — only re-renders when book/language/expand state changes ─
// Extracted so that verse loading, font changes, and popup state don't cause
// the 66-book list to re-reconcile.
const BibleSidebar = React.memo(function BibleSidebar({
  displayLang, selectedBook, selectedChapter, mobileView,
  ntExpanded, otExpanded, expandedBooks,
  onToggleNt, onToggleOt, onToggleBook, onSelectBook, onOpenChapter,
}) {
  const t = pick(bibleText, displayLang);
  return (
    <div className={`bible-sidebar${mobileView !== 'books' ? ' bible-hidden-mobile' : ''}`}>
      <div className="bible-testament-label bible-testament-collapse" onClick={onToggleNt}>
        <span className="bible-collapse-arrow">{ntExpanded ? '▾' : '▸'}</span>
        {t.nt}
      </div>
      {ntExpanded && NT_BOOKS.map(abbr => (
        <div key={abbr} className="bible-book-item">
          <div className="bible-book-row">
            <button className="bible-book-expand-btn" onClick={e => { e.stopPropagation(); onToggleBook(abbr); }}>
              {expandedBooks.has(abbr) ? '▾' : '▸'}
            </button>
            <button className={`bible-book-btn${selectedBook === abbr ? ' active' : ''}`} onClick={() => onSelectBook(abbr)}>
              {bookName(abbr, displayLang)}
            </button>
          </div>
          {expandedBooks.has(abbr) && (
            <div className="bible-sidebar-chapter-grid">
              {chaptersForBook(abbr).map(ch => (
                <button key={ch}
                  className={`bible-sidebar-ch-btn${selectedBook === abbr && selectedChapter === ch ? ' active' : ''}`}
                  onClick={() => onOpenChapter(abbr, ch)}>
                  {ch}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="bible-testament-label bible-testament-collapse" style={{ marginTop: '8px' }} onClick={onToggleOt}>
        <span className="bible-collapse-arrow">{otExpanded ? '▾' : '▸'}</span>
        {t.ot}
      </div>
      {otExpanded && OT_BOOKS.map(abbr => (
        <div key={abbr} className="bible-book-item">
          <div className="bible-book-row">
            <button className="bible-book-expand-btn" onClick={e => { e.stopPropagation(); onToggleBook(abbr); }}>
              {expandedBooks.has(abbr) ? '▾' : '▸'}
            </button>
            <button className={`bible-book-btn${selectedBook === abbr ? ' active' : ''}`} onClick={() => onSelectBook(abbr)}>
              {bookName(abbr, displayLang)}
            </button>
          </div>
          {expandedBooks.has(abbr) && (
            <div className="bible-sidebar-chapter-grid">
              {chaptersForBook(abbr).map(ch => (
                <button key={ch}
                  className={`bible-sidebar-ch-btn${selectedBook === abbr && selectedChapter === ch ? ' active' : ''}`}
                  onClick={() => onOpenChapter(abbr, ch)}>
                  {ch}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
});

// ── Landing book grid ───────────────────────────────────────────────────────
// Shown when no book is selected: a centered, tap-friendly grid of all books
// grouped by testament — much faster to pick from than the vertical sidebar.
const BibleBookGrid = React.memo(function BibleBookGrid({ displayLang, onSelectBook }) {
  const t = pick(bibleText, displayLang);
  const section = (heading, books) => (
    <div className="bible-grid-section">
      <h2 className="bible-grid-heading">{heading}</h2>
      <div className="bible-grid">
        {books.map(abbr => (
          <button key={abbr} className="bible-grid-btn" onClick={() => onSelectBook(abbr)}>
            {bookName(abbr, displayLang)}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="bible-landing">
      <div className="bible-landing-inner">
        {section(t.nt, NT_BOOKS)}
        {section(t.ot, OT_BOOKS)}
      </div>
    </div>
  );
});

const LANG_OPTIONS = [
  { code: 'en',  label: 'EN English'        },
  { code: 'es',  label: 'ES Español'        },
  { code: 'zh',  label: '繁 Traditional'    },
  { code: 'sc',  label: '简 Simplified'     },
];

const langLabel = (code) => LANG_OPTIONS.find(l => l.code === code)?.label || code;

function bookName(abbr, lang) {
  return BOOK_NAMES[abbr]?.[lang] || BOOK_NAMES[abbr]?.en || abbr;
}



// ── Component ──────────────────────────────────────────────────────────────

export default function Bible({ lang }) {
  // URL is the source of truth: /bible?book=Lk&ch=8. Deriving (not mirroring to
  // state) keeps book/chapter in sync with the URL with no render lag, and makes
  // "The Bible" nav → bare /bible a real navigation back to the grid.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBook    = searchParams.get('book') || null;
  const selectedChapter = searchParams.get('ch') ? parseInt(searchParams.get('ch'), 10) : null;
  const showIntro       = !!selectedBook && !selectedChapter;
  const mobileView      = !selectedBook ? 'books' : selectedChapter ? 'verses' : 'chapters';

  const [chapterData, setChapterData]         = useState(null);
  const [chapterLoading, setChapterLoading]   = useState(false);
  // Which chapter chapterData holds ("Gen:12"), so a page turn knows when the
  // chapter it turned to is really on screen.
  const [loadedKey, setLoadedKey]             = useState(null);
  // Set by a page turn, which scrolls the new chapter into place itself.
  const turnScrolled = useRef(false);

  // Chapter list derived directly — chaptersForBook is O(1), no state needed
  const chapters = useMemo(() => selectedBook ? chaptersForBook(selectedBook) : [], [selectedBook]);

  const [displayLang, setDisplayLang]       = useState(() => localStorage.getItem('bibleAppLang') || 'en');
  const [fontSize, setFontSize]             = useState(() => parseInt(localStorage.getItem('bibleFontSize'), 10) || 18);
  const [parallelMode, setParallelMode]     = useState(() => localStorage.getItem('parallelMode') === 'true');
  const [parallelLangA, setParallelLangA]   = useState(() => localStorage.getItem('parallelLangA') || lang || 'en');
  const [parallelLangB, setParallelLangB]   = useState(() => localStorage.getItem('parallelLangB') || 'zh');

  // ── Book list collapse state ─────────────────────────────────────────────
  const [ntExpanded, setNtExpanded] = useState(true);
  const [otExpanded, setOtExpanded] = useState(true);

  // On a phone the chapter grid and verse strip give way to the three selects
  // in the bar, so the screen belongs to the text. Desktop has the room and
  // shows them as before.
  //
  // Font size, parallel, references and outline fold into one menu: they are
  // adjustments to how the chapter reads, set once and rarely revisited.
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef(null);

  // Tapping anywhere else puts the menu away. Without this the only way out is
  // to find the button again, which on a phone means aiming at a 32px target
  // you have already stopped looking at.
  useEffect(() => {
    if (!viewMenuOpen) return;
    const away = (e) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setViewMenuOpen(false);
    };
    const esc = (e) => { if (e.key === 'Escape') setViewMenuOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [viewMenuOpen]);

  // ── Sidebar expandable books + intro/outline state ───────────────────────
  const [expandedBooks, setExpandedBooks] = useState(new Set());
  const [bookIntro, setBookIntro]         = useState(null);
  const [bookOutline, setBookOutline]     = useState([]);
  const [introFetching, setIntroFetching] = useState(false);
  // Which book and language bookIntro holds ("Exo:en"), so a page turn to an
  // outline page knows when that page is really on screen.
  const [introKey, setIntroKey]           = useState(null);
  const [outlineExpandTrigger, setOutlineExpandTrigger]   = useState(0);
  const [outlineCollapseTrigger, setOutlineCollapseTrigger] = useState(0);
  const [outlineAllExpanded, setOutlineAllExpanded]         = useState(false);
  const [chapterOutlines, setChapterOutlines]   = useState([]);
  const [chapterOutlinesB, setChapterOutlinesB] = useState([]);
  const [showOutline, setShowOutline] = useState(() => localStorage.getItem('showOutline') === 'true');

  const pendingScrollVerse = useRef(null);

  // ── Refs mode (cross-references and footnotes) ──────────────────────────
  // Admins only for now; this becomes `true` for everyone once refs are opened
  // up. The flag only decides what is shown — the refs tables are publicly
  // readable anyway — so trusting the client-side isAdmin here is fine.
  // Read from context on every render, so signing in or out shows or hides
  // the toggle straight away.
  const { user } = useUser();
  const canSeeRefs = !!user?.isAdmin;
  const [showRefs, setShowRefs]       = useState(false);
  const [refsMap, setRefsMap]         = useState({});  // column A: { "verse_marker": {type,content} }
  const [markedTexts, setMarkedTexts] = useState({});  // column A: { verseNum: markedString }
  const [refsMapB, setRefsMapB]       = useState({});  // column B (parallel)
  const [markedTextsB, setMarkedTextsB] = useState({}); // column B (parallel)
  const popups = useRefPopups();
  const { pushPopup, closeAllPopups, popupContextValue } = popups;

  // Losing access (signing out) turns the markers off rather than leaving them
  // drawn with no button to hide them.
  useEffect(() => { if (!canSeeRefs) setShowRefs(false); }, [canSeeRefs]);

  // Publish the sticky toolbar's height so verse-jump targets land below it
  // (via scroll-margin-top) instead of behind it.
  useEffect(() => {
    const el = document.querySelector('.bible-sticky-toolbar');
    const root = document.documentElement;
    if (!el) { root.style.setProperty('--bible-toolbar-h', '0px'); return; }
    const set = () => root.style.setProperty('--bible-toolbar-h', el.offsetHeight + 'px');
    set();
    const obs = new ResizeObserver(set);
    obs.observe(el);
    return () => { obs.disconnect(); root.style.setProperty('--bible-toolbar-h', '0px'); };
  }, [selectedChapter, parallelMode, chapterData]);

  // Load chapter text whenever the selected book+chapter (from the URL) changes.
  useEffect(() => {
    if (!selectedBook || !selectedChapter) { setChapterData(null); setLoadedKey(null); return; }
    const key = `${selectedBook}:${selectedChapter}`;
    setChapterOutlines([]);
    setChapterOutlinesB([]);
    if (turnScrolled.current) turnScrolled.current = false;
    else setTimeout(() => verseContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    // A chapter already in the cache (the ones either side are fetched ahead
    // for page turns) is drawn straight away, with no "Loading…" in between.
    const cached = getChapterCache(selectedBook, selectedChapter);
    if (cached && chapterCols.split(',').every(c => cached[c] !== undefined)) {
      setChapterData(cached);
      setChapterLoading(false);
      setLoadedKey(key);
      return;
    }
    setChapterData(null);
    setChapterLoading(true);
    let cancelled = false;
    fetchChapterData(selectedBook, selectedChapter, chapterCols)
      .then(data => { if (!cancelled) setChapterData(data); })
      .catch(() => { if (!cancelled) setChapterData(null); })
      .finally(() => { if (!cancelled) { setChapterLoading(false); setLoadedKey(key); } });
    return () => { cancelled = true; };
  }, [selectedBook, selectedChapter]); // eslint-disable-line react-hooks/exhaustive-deps

  const verseContentRef = useRef(null);
  // Holds the drawn verses, where selecting text opens the highlight and note
  // toolbar (src/annotations/).
  const annoRootRef = useRef(null);

  // Which refs lang is active — null means no refs for that lang (e.g. Spanish)
  const activeRefsLang = useMemo(() => {
    const l = parallelMode ? parallelLangA : displayLang;
    return (l === 'en' || l === 'zh' || l === 'sc') ? l : null;
  }, [parallelMode, parallelLangA, displayLang]);

  // Refs lang for parallel column B (only meaningful in parallel mode)
  const activeRefsLangB = useMemo(() => {
    if (!parallelMode) return null;
    return (parallelLangB === 'en' || parallelLangB === 'zh' || parallelLangB === 'sc') ? parallelLangB : null;
  }, [parallelMode, parallelLangB]);

  const t = useMemo(() => pick(bibleText, displayLang), [displayLang]);

  const introLang = useMemo(() => (displayLang === 'es' ? 'en' : displayLang), [displayLang]);

  // Effective outline language for column A: use parallelLangA when in parallel mode
  const outlineLangA = useMemo(() => {
    if (parallelMode) return parallelLangA === 'es' ? 'en' : parallelLangA;
    return introLang;
  }, [parallelMode, parallelLangA, introLang]);

  // Outline language for parallel column B (null = same as A, no second fetch needed)
  const outlineLangB = useMemo(() => {
    if (!parallelMode) return null;
    const lang = parallelLangB === 'es' ? 'en' : parallelLangB;
    return lang === outlineLangA ? null : lang;
  }, [parallelMode, parallelLangB, outlineLangA]);

  // Persist preferences — single effect, runs only when a relevant value changes
  useEffect(() => {
    localStorage.setItem('bibleFontSize', String(fontSize));
    localStorage.setItem('parallelMode',  String(parallelMode));
    localStorage.setItem('parallelLangA', parallelLangA);
    localStorage.setItem('parallelLangB', parallelLangB);
    localStorage.setItem('showOutline',   String(showOutline));
  }, [fontSize, parallelMode, parallelLangA, parallelLangB, showOutline]);

  // Columns to fetch for chapter text — only what the current display config needs
  const chapterCols = useMemo(() => {
    const audioCol = l => (l === 'sc' || l === 'zh') ? 'audio_zh' : l === 'es' ? 'audio_es' : 'audio_en';
    const cols = new Set();
    if (parallelMode) {
      cols.add(`text_${parallelLangA}`); cols.add(`text_${parallelLangB}`);
      cols.add(audioCol(parallelLangA));  cols.add(audioCol(parallelLangB));
    } else {
      cols.add(`text_${displayLang}`);
      cols.add(audioCol(displayLang));
    }
    return [...cols].join(',');
  }, [displayLang, parallelMode, parallelLangA, parallelLangB]);

  // Re-fetch chapter data when language/mode changes and the needed column isn't cached yet
  useEffect(() => {
    if (!selectedBook || !selectedChapter || !chapterData) return;
    const colArr = chapterCols.split(',');
    if (colArr.every(c => chapterData[c] !== undefined)) return; // already have all needed cols
    fetchChapterData(selectedBook, selectedChapter, chapterCols)
      .then(data => setChapterData(data))
      .catch(() => {});
  }, [chapterCols]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch chapter outlines for A (and B when needed) in a single DB round-trip.
  // Always fetched so the § Outline toggle button appears; rendering gated on showOutline.
  useEffect(() => {
    if (!selectedBook || !selectedChapter) {
      setChapterOutlines([]); setChapterOutlinesB([]); return;
    }

    const cachedA = getOutlineCache(selectedBook, selectedChapter, outlineLangA);
    const needsB  = !!outlineLangB;
    const cachedB = needsB ? getOutlineCache(selectedBook, selectedChapter, outlineLangB) : null;

    // Apply any cached data immediately
    if (cachedA !== null) setChapterOutlines(cachedA);
    if (!needsB) setChapterOutlinesB([]);
    else if (cachedB !== null) setChapterOutlinesB(cachedB);

    const needA = cachedA === null;
    const needB = needsB && cachedB === null;
    if (!needA && !needB) return;

    // One query covers both langs when both are missing
    const langsToFetch = [...(needA ? [outlineLangA] : []), ...(needB ? [outlineLangB] : [])];
    supabase.from('bible_outlines').select(OUTLINE_COLS)
      .eq('book_abbr', selectedBook).in('lang', langsToFetch).eq('start_chapter', selectedChapter)
      .order('sort_order')
      .then(({ data }) => {
        const rows = data || [];
        if (needA) {
          const result = rows.filter(r => r.lang === outlineLangA);
          setOutlineCache(selectedBook, selectedChapter, outlineLangA, result);
          setChapterOutlines(result);
        }
        if (needB) {
          const result = rows.filter(r => r.lang === outlineLangB);
          setOutlineCache(selectedBook, selectedChapter, outlineLangB, result);
          setChapterOutlinesB(result);
        }
      });
  }, [outlineLangA, outlineLangB, selectedBook, selectedChapter]);

  // Sync both display lang and (unoverridden) parallelLangA when the lang prop changes
  useEffect(() => {
    setDisplayLang(lang);
    if (!localStorage.getItem('parallelLangA')) setParallelLangA(lang || 'en');
  }, [lang]);

  // NT_BOOKS / OT_BOOKS are the complete static lists — no runtime filtering needed

  // Re-fetch intro + outline whenever book or language changes; caches in
  // sessionStorage (the book coming up is fetched ahead for page turns).
  useEffect(() => {
    if (!selectedBook) { setBookIntro(null); setBookOutline([]); setIntroKey(null); return; }
    const key = `${selectedBook}:${introLang}`;
    const cached = getBookIntroCache(selectedBook, introLang);
    if (cached) {
      setBookIntro(cached.intro);
      setBookOutline(cached.outline);
      setOutlineAllExpanded(false);
      setIntroFetching(false);
      setIntroKey(key);
      return;
    }
    setIntroFetching(true);
    let cancelled = false;
    fetchBookIntro(selectedBook, introLang).then(({ intro, outline }) => {
      if (cancelled) return;
      setBookIntro(intro);
      setBookOutline(outline);
      setOutlineAllExpanded(false);
    }).catch(() => {
      if (!cancelled) { setBookIntro(null); setBookOutline([]); }
    }).finally(() => {
      if (!cancelled) { setIntroFetching(false); setIntroKey(key); }
    });
    return () => { cancelled = true; };
  }, [selectedBook, introLang]);

  // Handlers only update the URL; the [searchParams] effect drives state + fetch.
  const selectBook = useCallback((abbr) => {
    setSearchParams({ book: abbr });
  }, [setSearchParams]);

  const selectChapter = useCallback((ch, bookOverride) => {
    const bookAbbr = bookOverride || searchParams.get('book');
    if (bookAbbr) setSearchParams({ book: bookAbbr, ch: String(ch) });
    setViewMenuOpen(false);         // picked one — get out of the reader's way
  }, [searchParams, setSearchParams]);

  const goTo = useCallback((target) => {
    if (!target) return;
    setSearchParams(target.ch ? { book: target.book, ch: String(target.ch) } : { book: target.book });
    setViewMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });   // land at the start, not mid-chapter
  }, [setSearchParams]);

  // ── Turning chapters like pages (src/pageturn/) ──────────────────────────
  // Pages run through the whole Bible in order, and each book opens with its
  // outline page: Genesis 50 turns to the Exodus outline, which turns to
  // Exodus 1; back from Exodus 1 is the Exodus outline, then Genesis 50. The
  // Genesis outline is the first page and Revelation 22 the last
  // (src/bible/chapterSequence.js).
  const turnPrev = useMemo(() => (selectedBook ? stepPage(selectedBook, selectedChapter, -1) : null),
    [selectedBook, selectedChapter]);
  const turnNext = useMemo(() => (selectedBook ? stepPage(selectedBook, selectedChapter, 1) : null),
    [selectedBook, selectedChapter]);

  // Fetch a page ahead of time, so turning to it slides in its real text (a
  // chapter) or its real introduction and outline (an outline page).
  const prefetchPage = useCallback((target) => {
    if (!target?.book) return;
    if (target.ch) fetchChapterData(target.book, target.ch, chapterCols).catch(() => {});
    else fetchBookIntro(target.book, introLang).catch(() => {});
  }, [chapterCols, introLang]);

  const onTurn = useCallback((dir, target, { step = 0 } = {}) => {
    const dest = target || (dir > 0 ? turnNext : turnPrev);
    if (!dest) return;
    // Between chapters the page turn scrolls the new one into place itself.
    // From an outline page the view changes shape, so the usual scroll to the
    // chapter still runs; and an outline page is read from its top.
    turnScrolled.current = !!(selectedChapter && dest.ch);
    if (!dest.ch) window.scrollTo(0, 0);
    setViewMenuOpen(false);
    // A held arrow key turns many pages; keep them out of the back button,
    // and fetch the page after this one now, since there is no pause in
    // which the usual fetch-ahead would run.
    setSearchParams(dest.ch ? { book: dest.book, ch: String(dest.ch) } : { book: dest.book }, { replace: step > 0 });
    if (step > 0) prefetchPage(stepPage(dest.book, dest.ch, dir));
  }, [turnNext, turnPrev, selectedChapter, setSearchParams, prefetchPage]);

  // The page that slides in beside this one: real text when it is cached
  // (the neighbours are fetched ahead, below), else its name over grey lines.
  const turnLangsKey = parallelMode ? `${parallelLangA},${parallelLangB}` : displayLang;
  const renderNeighbour = useCallback((dir, target) => {
    const n = target || (dir > 0 ? turnNext : turnPrev);
    if (!n) return null;
    const name = bookName(n.book, displayLang);
    if (!n.ch) {
      return <OutlinePreview book={n.book} lang={displayLang} introLang={introLang} t={t} label={t.outlinePage(name)} />;
    }
    // The audio row's height, so the text starts where the real page's will.
    const row = document.querySelector('.bible-verse-view .bible-audio-single, .bible-verse-view .parallel-audio-row');
    return (
      <ChapterPreview book={n.book} ch={n.ch} langs={turnLangsKey.split(',')} fontSize={fontSize}
        audioHeight={row ? row.offsetHeight : 0}
        audioClass={parallelMode ? 'parallel-audio-row' : 'bible-audio-single'}
        label={t.chapterLabel(name, n.ch)} />
    );
  }, [turnNext, turnPrev, t, displayLang, introLang, turnLangsKey, fontSize, parallelMode]);


  const pageKey = `${selectedBook}:${selectedChapter || 'intro'}`;
  const turner = usePageTurn({
    canPrev: !!turnPrev,
    canNext: !!turnNext,
    onTurn,
    renderNeighbour,
    pageKey,
    // An outline page is ready once its book's introduction is in. The book
    // list (no book chosen) has nothing to wait for; without this, tapping
    // "Books" right after a turn left the cover up until its timeout.
    ready: !selectedBook
      || (selectedChapter
        ? loadedKey === pageKey && !chapterLoading
        : introKey === `${selectedBook}:${introLang}`),
    getTopInset: stickyBottom,
    keyboard: !!selectedBook,
  });
  const turnPage = turner.turn;

  // Fetch the pages either side once this one is in, so a turn slides in
  // real text. They land in the sessionStorage caches the loaders read first.
  // An outline page is small, so the next book's is fetched from two pages
  // away: on the second-to-last chapter, and on chapter 2 for this book's.
  useEffect(() => {
    if (!selectedBook || (selectedChapter && !chapterData)) return;
    const id = setTimeout(() => {
      [turnNext, turnPrev].forEach(prefetchPage);
      [turnNext && stepPage(turnNext.book, turnNext.ch, 1), turnPrev && stepPage(turnPrev.book, turnPrev.ch, -1)]
        .forEach(n => { if (n && !n.ch) prefetchPage(n); });
    }, 250);
    return () => clearTimeout(id);
  }, [selectedBook, selectedChapter, chapterData, turnNext, turnPrev, prefetchPage]);

  // The scrubber's choice: one turn, forwards or back, straight there.
  const jumpTo = useCallback((target) => {
    const dir = compareChapters(target, { book: selectedBook, ch: selectedChapter });
    if (!dir) return;
    turnPage(dir, { target });
  }, [selectedBook, selectedChapter, turnPage]);

  const toggleNt = useCallback(() => setNtExpanded(e => !e), []);
  const toggleOt = useCallback(() => setOtExpanded(e => !e), []);

  const toggleBookExpand = useCallback((abbr) => {
    setExpandedBooks(prev => {
      const next = new Set(prev);
      if (next.has(abbr)) next.delete(abbr);
      else next.add(abbr);
      return next;
    });
  }, []);

  const openChapter = useCallback((abbr, ch) => {
    setSearchParams({ book: abbr, ch: String(ch) });
  }, [setSearchParams]);

  const scrollToVerse = useCallback((n) => {
    const el = document.getElementById(`bible-verse-${n}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const getAudioSrc = useCallback((l) => {
    if (!chapterData) return null;
    if (l === 'sc') return chapterData.audio_zh || null;
    return chapterData[`audio_${l}`] || null;
  }, [chapterData]);

  // ── Verse timings: playing the chapter's recording from a verse ──────────
  // Column A's recording (the only one outside parallel mode) and column B's.
  // A recording without a timing row, or in a language without timings,
  // keeps today's plain player and plain verse numbers.
  const audioLangA = parallelMode ? parallelLangA : displayLang;
  const audioSrcA = getAudioSrc(audioLangA);
  const audioSrcB = parallelMode ? getAudioSrc(parallelLangB) : null;
  const audioElA = useRef(null);
  const audioElB = useRef(null);
  const [timedA] = useTimingRows(useMemo(() => [audioSrcA], [audioSrcA]), timingLangFor(audioLangA));
  const [timedB] = useTimingRows(useMemo(() => [audioSrcB], [audioSrcB]), timingLangFor(parallelLangB));
  // A row filed under another chapter would seek to the wrong place.
  const rowA = rowIs(timedA, selectedBook, selectedChapter) ? timedA : null;
  const rowB = parallelMode && rowIs(timedB, selectedBook, selectedChapter) ? timedB : null;
  const ta = useMemo(() => pick(annotationsText, displayLang), [displayLang]);

  // ── The verse being read: lit and followed while a recording plays ──────
  // Each <audio> reports as its column ('a' or 'b'); a timeupdate only
  // reaches React state-free code here, so the chapter does not re-render
  // four times a second.
  const follow = useFollowVerse(annoRootRef, FOLLOW_HEADERS);
  const followReset = follow.reset;
  // A new chapter, language or layout means new <audio> elements: forget the
  // old ones' verse (they never report their own end when swapped out).
  useEffect(() => { followReset(); },
    [followReset, selectedBook, selectedChapter, audioLangA, audioSrcA, parallelMode, parallelLangB, audioSrcB]);
  const audioEvents = (column, row) => {
    const report = e => {
      const verse = row ? verseAt(row, e.currentTarget.currentTime) : null;
      follow.onVerse(column, verse == null ? null : { book: row.book, chapter: row.chapter, verse });
    };
    return {
      onTimeUpdate: report,
      onSeeked: report,
      onPlay: () => follow.onPlaying(column, true),
      onPause: e => { if (!e.currentTarget.ended) follow.onPlaying(column, false); },
      onEnded: () => { follow.onPlaying(column, false); follow.onVerse(column, null); },
    };
  };

  const playVerse = useCallback((column, verse) => {
    const row = column === 'b' ? rowB : rowA;
    const el = column === 'b' ? audioElB.current : audioElA.current;
    if (row && el && hasVerse(row, verse)) playAt(el, verseStart(row, verse) || 0);
  }, [rowA, rowB]);

  // "Listen" on a selection: the column the selection is in, from its first verse.
  const listenTarget = ranges => {
    if (!ranges?.length) return null;
    const first = [...ranges].sort(compareVerses)[0];
    if (first.book !== selectedBook || first.chapter !== selectedChapter) return null;
    const column = !parallelMode || first.lang === parallelLangA ? 'a' : first.lang === parallelLangB ? 'b' : null;
    const row = column === 'a' ? rowA : column === 'b' ? rowB : null;
    return row && hasVerse(row, first.verse) ? { column, verse: first.verse } : null;
  };
  const canListen = ranges => !!listenTarget(ranges);
  const listenFrom = ranges => {
    const target = listenTarget(ranges);
    if (!target) return;
    playVerse(target.column, target.verse);
    window.getSelection?.()?.removeAllRanges();
  };

  // ── Refs data fetching (admin only) ────────────────────────────────────────
  useEffect(() => {
    if (!showRefs || !selectedBook || !selectedChapter) {
      setRefsMap({}); setMarkedTexts({});
      setRefsMapB({}); setMarkedTextsB({});
      return;
    }

    // Helper: fetch refs + marked text for one lang
    async function fetchLangData(lang) {
      if (!lang) return { map: {}, texts: {} };
      const col = lang === 'sc' ? 'text_sc_marked' : lang === 'zh' ? 'text_zh_marked' : 'text_en_marked';
      const [{ data: refs }, { data: ch }] = await Promise.all([
        supabase.from('bible_refs').select('verse,marker,type,content')
          .eq('book_abbr', selectedBook).eq('chapter', selectedChapter).eq('lang', lang),
        supabase.from('bible_chapters').select(col)
          .eq('book_abbr', selectedBook).eq('chapter', selectedChapter).single(),
      ]);
      const map = {};
      (refs || []).forEach(r => { map[`${r.verse}_${r.marker}`] = { type: r.type, content: r.content }; });
      const texts = {};
      if (ch?.[col]) {
        ch[col].split('\n').forEach(line => {
          const mm = line.match(/^(\d+)\s+([\s\S]*)/);
          if (mm) texts[parseInt(mm[1])] = mm[2];
        });
      }
      return { map, texts };
    }

    (async () => {
      const sameLang = !!(activeRefsLangB && activeRefsLangB === activeRefsLang);
      const [aData, bData] = await Promise.all([
        fetchLangData(activeRefsLang),
        sameLang ? Promise.resolve(null) : fetchLangData(activeRefsLangB),
      ]);
      setRefsMap(aData.map);
      setMarkedTexts(aData.texts);
      if (sameLang) {
        setRefsMapB(aData.map);
        setMarkedTextsB(aData.texts);
      } else if (bData) {
        setRefsMapB(bData.map);
        setMarkedTextsB(bData.texts);
      } else {
        setRefsMapB({}); setMarkedTextsB({});
      }
    })();
  }, [showRefs, selectedBook, selectedChapter, activeRefsLang, activeRefsLangB]);

  // ── Pending scroll to verse after chapter load ────────────────────────────
  useEffect(() => {
    if (pendingScrollVerse.current !== null && selectedChapter && chapterData) {
      const v = pendingScrollVerse.current;
      pendingScrollVerse.current = null;
      setTimeout(() => document.getElementById(`bible-verse-${v}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [selectedChapter, chapterData]);

  // ── Ref markers inside a verse ───────────────────────────────────────────
  // Returns the renderMarker(marker, key) that AnnotatedVerse calls for each
  // "[marker]" in a verse's marked text; overrideRefsMap/overrideLang for
  // parallel col B. selectedBook + selectedChapter are in deps so popup
  // context is never stale.
  const markerRenderer = useCallback((verseNum, overrideRefsMap, overrideLang) => (marker, key) => {
    const localMap  = overrideRefsMap || refsMap;
    const localLang = overrideLang    || activeRefsLang;
    const ref = localMap[`${verseNum}_${marker}`];
    const isLetter = /^[a-z]/i.test(marker);
    return (
      <sup
        key={key}
        className={`bible-ref-sup${ref ? (isLetter ? ' bible-ref-cr' : ' bible-ref-fn') : ''}`}
        title={ref ? (ref.type === 'cr' ? '📖 ' : '📝 ') + ref.content.slice(0, 80) + (ref.content.length > 80 ? '…' : '') : ''}
        onClick={ref ? e => {
          e.stopPropagation();
          const rect = e.target.getBoundingClientRect();
          pushPopup({
            kind:           'note',
            title:          isLetter ? '📖 Cross-reference' : '📝 Footnote',
            content:        ref.content,
            contentLang:    localLang || 'en',
            contextBook:    selectedBook,
            contextChapter: selectedChapter,
            x: rect.left,
            y: rect.bottom + 6,
          });
        } : undefined}
      >
        {marker}
      </sup>
    );
  }, [refsMap, pushPopup, activeRefsLang, selectedBook, selectedChapter]);

  // ── Memoised parsed verse arrays — avoids re-parsing on font/popup/ref changes ──
  const parsedVerses  = useMemo(() => parseStoredVerses(chapterData?.[`text_${displayLang}`]),   [chapterData, displayLang]);
  const parsedVersesA = useMemo(() => parallelMode ? parseStoredVerses(chapterData?.[`text_${parallelLangA}`]) : [], [parallelMode, chapterData, parallelLangA]);
  const parsedVersesB = useMemo(() => parallelMode ? parseStoredVerses(chapterData?.[`text_${parallelLangB}`]) : [], [parallelMode, chapterData, parallelLangB]);
  // Psalm superscriptions / Ps 119 stanza letters, keyed by the verse they sit above
  const headingsByVerse  = useMemo(() => parseStoredHeadings(chapterData?.[`text_${displayLang}`]),   [chapterData, displayLang]);
  const headingsByVerseA = useMemo(() => parallelMode ? parseStoredHeadings(chapterData?.[`text_${parallelLangA}`]) : {}, [parallelMode, chapterData, parallelLangA]);
  const headingsByVerseB = useMemo(() => parallelMode ? parseStoredHeadings(chapterData?.[`text_${parallelLangB}`]) : {}, [parallelMode, chapterData, parallelLangB]);
  // verse-number → text map for parallel column B (avoids rebuilding inside renderVersesParallel)
  const verseMapB = useMemo(() => {
    const m = {};
    parsedVersesB.forEach(v => { m[v.verse] = v.text; });
    return m;
  }, [parsedVersesB]);

  // ── Verse renderers — stable useCallback so useMemo output can skip re-renders ──
  // Both receive pre-computed maps (byVerse, verseMapB) to avoid rebuilding inside.

  const renderVerses = useCallback((verses, byVerse, headings = {}) => {
    if (verses.length === 0) return <p style={{ opacity: 0.5, padding: '12px 0' }}>{t.noText}</p>;
    return verses.map(({ verse, text: vt }) => {
      const headers   = byVerse[verse] || [];
      const useMarked = showRefs && markedTexts[verse];
      // Highlights and notes are anchored to the plain text and drawn over
      // the marked text too, so they stay put when Refs is switched on.
      const content   = (
        <AnnotatedVerse book={selectedBook} chapter={selectedChapter} verse={verse} lang={displayLang}
          text={vt} marked={useMarked ? markedTexts[verse] : null}
          renderMarker={useMarked ? markerRenderer(verse) : undefined} />
      );
      return (
        <React.Fragment key={verse}>
          {headers.map((ol, i) => renderOutlineHeader(ol, `ol-${ol.id || i}`, scrollToVerse))}
          {(headings[verse] || []).map((h, i) => (
            <div key={`sup-${i}`} className="bible-superscription" style={{ fontSize: fontSize + 'px' }}>{h}</div>
          ))}
          <div id={`bible-verse-${verse}`} {...verseAttrs(selectedBook, selectedChapter, verse, displayLang)}
            style={{ display: 'flex', gap: '10px', marginBottom: '8px', lineHeight: 1.75, fontSize: fontSize + 'px' }}>
            <VerseNumber verse={verse} label={ta.playFromVerse(verse)}
              onPlay={hasVerse(rowA, verse) ? () => playVerse('a', verse) : null} />
            <span>{content}</span>
          </div>
        </React.Fragment>
      );
    });
  }, [showRefs, markedTexts, fontSize, t, scrollToVerse, markerRenderer, selectedBook, selectedChapter, displayLang,
      rowA, playVerse, ta]);

  const renderVersesParallel = useCallback((versesA, byVerseA, byVerseB, mapB, headingsA = {}, headingsB = {}) => {
    if (versesA.length === 0) return <p style={{ opacity: 0.5 }}>{t.noText}</p>;
    return versesA.map(({ verse, text: vt }) => {
      const headersA   = byVerseA[verse] || [];
      const headersB   = byVerseB[verse] || [];
      const hasHeaders = headersA.length > 0 || headersB.length > 0;
      const supA       = headingsA[verse] || [];
      const supB       = headingsB[verse] || [];
      // Column A — uses activeRefsLang (parallelLangA)
      const markedA = showRefs && activeRefsLang && markedTexts[verse];
      const contentA = (
        <AnnotatedVerse book={selectedBook} chapter={selectedChapter} verse={verse} lang={parallelLangA}
          text={vt} marked={markedA || null}
          renderMarker={markedA ? markerRenderer(verse) : undefined} />
      );
      // Column B — uses activeRefsLangB + its own refsMap / markedTexts
      const markedB = showRefs && activeRefsLangB && markedTextsB[verse];
      const contentB = (
        <AnnotatedVerse book={selectedBook} chapter={selectedChapter} verse={verse} lang={parallelLangB}
          text={mapB[verse] || ''} marked={markedB || null}
          renderMarker={markedB ? markerRenderer(verse, refsMapB, activeRefsLangB) : undefined} />
      );
      return (
        <React.Fragment key={verse}>
          {hasHeaders && (
            <div className="parallel-row" style={{ marginBottom: '2px' }}>
              <div className="parallel-col">
                {headersA.map((ol, i) => renderOutlineHeader(ol, `olA-${ol.id || i}`, scrollToVerse))}
              </div>
              <div className="parallel-col">
                {headersB.map((ol, i) => renderOutlineHeader(ol, `olB-${ol.id || i}`, scrollToVerse))}
              </div>
            </div>
          )}
          {(supA.length > 0 || supB.length > 0) && (
            <div className="parallel-row">
              <div className="parallel-col">
                {supA.map((h, i) => <div key={i} className="bible-superscription" style={{ fontSize: fontSize + 'px' }}>{h}</div>)}
              </div>
              <div className="parallel-col">
                {supB.map((h, i) => <div key={i} className="bible-superscription" style={{ fontSize: fontSize + 'px' }}>{h}</div>)}
              </div>
            </div>
          )}
          <div id={`bible-verse-${verse}`} className="parallel-row" style={{ marginBottom: '8px' }}>
            <div className={`parallel-col bible-text-${parallelLangA}`} {...verseAttrs(selectedBook, selectedChapter, verse, parallelLangA)} style={{ display: 'flex', gap: '10px', lineHeight: 1.75, fontSize: fontSize + 'px' }}>
              <VerseNumber verse={verse} label={ta.playFromVerse(verse)}
                onPlay={hasVerse(rowA, verse) ? () => playVerse('a', verse) : null} />
              <span>{contentA}</span>
            </div>
            <div className={`parallel-col bible-text-${parallelLangB}`} {...verseAttrs(selectedBook, selectedChapter, verse, parallelLangB)} style={{ display: 'flex', gap: '10px', lineHeight: 1.75, fontSize: fontSize + 'px' }}>
              <VerseNumber verse={verse} label={ta.playFromVerse(verse)}
                onPlay={hasVerse(rowB, verse) ? () => playVerse('b', verse) : null} />
              <span>{contentB}</span>
            </div>
          </div>
        </React.Fragment>
      );
    });
  }, [showRefs, markedTexts, markedTextsB, refsMapB, fontSize, t, scrollToVerse, markerRenderer,
      activeRefsLang, activeRefsLangB, parallelLangA, parallelLangB, selectedBook, selectedChapter,
      rowA, rowB, playVerse, ta]);

  // ── Outline tree (memoised) ──────────────────────────────────────────────
  // Enrich all outline items with inferred end refs (once, for reuse in both tree and inline view)
  const enrichedBookOutline = useMemo(() => inferEndRefs(bookOutline), [bookOutline]);
  const outlineTree = useMemo(() => buildOutlineTree(enrichedBookOutline), [enrichedBookOutline]);
  // Enrich chapter outlines independently (so ranges show even if book intro hasn't been loaded)
  const enrichedChapterOutlines  = useMemo(() => inferEndRefs(chapterOutlines),  [chapterOutlines]);
  const enrichedChapterOutlinesB = useMemo(() => parallelMode ? inferEndRefs(chapterOutlinesB) : [], [parallelMode, chapterOutlinesB]);
  // Pre-build verse→outlineItems maps (avoids rebuilding inside every render call)
  const outlinesByVerse  = useMemo(
    () => showOutline && enrichedChapterOutlines.length  ? buildOutlinesByVerse(enrichedChapterOutlines)  : {},
    [showOutline, enrichedChapterOutlines]
  );
  const outlinesByVerseB = useMemo(
    () => showOutline && parallelMode && enrichedChapterOutlinesB.length ? buildOutlinesByVerse(enrichedChapterOutlinesB) : {},
    [showOutline, parallelMode, enrichedChapterOutlinesB]
  );

  // Memoised rendered output — rebuilds only when verse data, outline maps, font, or refs change.
  // Popup opens / mobileSheet / isMobile changes no longer re-reconcile the verse list.
  const renderedVerses = useMemo(
    () => renderVerses(parsedVerses, outlinesByVerse, headingsByVerse),
    [renderVerses, parsedVerses, outlinesByVerse, headingsByVerse]
  );
  const renderedVersesParallel = useMemo(
    () => renderVersesParallel(parsedVersesA, outlinesByVerse, outlinesByVerseB, verseMapB, headingsByVerseA, headingsByVerseB),
    [renderVersesParallel, parsedVersesA, outlinesByVerse, outlinesByVerseB, verseMapB, headingsByVerseA, headingsByVerseB]
  );

  // Verse count for the jump strip — read directly from static table, no text parsing needed
  const verseCount = (selectedBook && selectedChapter)
    ? (BIBLE_VERSE_COUNTS[selectedBook]?.[selectedChapter - 1] ?? 0)
    : 0;

  // Verse jump strip buttons — only rebuilt when the chapter changes
  const verseJumpStrip = useMemo(() => (
    Array.from({ length: verseCount }, (_, i) => i + 1).map(n => (
      <button key={n} className="bible-verse-jump-btn" onClick={() => scrollToVerse(n)}>{n}</button>
    ))
  ), [verseCount, scrollToVerse]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PopupContext.Provider value={popupContextValue}>
    <>
    <RefPopupLayer popups={popups} />
    <AnnotationLayer rootRef={annoRootRef} lang={displayLang} onListen={listenFrom} canListen={canListen} />
    <FollowAudioPill visible={follow.pillVisible} onClick={follow.resume} label={ta.followAudio} />
    {turner.layer}
    {!selectedBook ? (
      <BibleBookGrid displayLang={displayLang} onSelectBook={selectBook} />
    ) : (
    <div className="bible-layout">

      {/* ── Books sidebar (memoized — only re-renders on book/lang/expand changes) ── */}
      <BibleSidebar
        displayLang={displayLang}
        selectedBook={selectedBook}
        selectedChapter={selectedChapter}
        mobileView={mobileView}
        ntExpanded={ntExpanded}
        otExpanded={otExpanded}
        expandedBooks={expandedBooks}
        onToggleNt={toggleNt}
        onToggleOt={toggleOt}
        onToggleBook={toggleBookExpand}
        onSelectBook={selectBook}
        onOpenChapter={openChapter}
      />

      {/* ── Main content ────────────────────────────────────── */}
      <div className={`bible-main${mobileView === 'books' ? ' bible-hidden-mobile' : ''}`}>

        {/* Mobile back — returns to the landing book grid */}
        <div className="bible-mobile-nav">
          <button className="bible-back-btn" onClick={() => setSearchParams({})}>← {t.books}</button>
        </div>

        {/* Book selected */}
        {selectedBook && (
          showIntro ? (
            /* ── Book intro (outline) page ── */
            <>
            <TurnPage turner={turner}>
            <div className="bible-intro-page">
              {/* Chapter grid at top */}
              <div className="bible-chapter-section">
                <div className="bible-chapter-section-header">
                  {/* The outline page carries the same arrows: forward to
                      chapter 1, back to the previous book's last chapter. */}
                  <button
                    className="bible-step-btn"
                    onClick={() => turnPage(-1)}
                    disabled={!turnPrev}
                    aria-label={t.prevChapter}
                  >
                    ‹
                  </button>
                  <h2 className="bible-book-heading">{bookName(selectedBook, displayLang)}</h2>
                  <button
                    className="bible-step-btn"
                    onClick={() => turnPage(1)}
                    disabled={!turnNext}
                    aria-label={t.nextChapter}
                  >
                    ›
                  </button>
                  <span className="bible-section-tag">{t.chapters}</span>
                </div>
                <div className="bible-chapter-grid">
                  {chapters.map(ch => (
                    <button key={ch} className="bible-ch-btn" onClick={() => selectChapter(ch)}>{ch}</button>
                  ))}
                </div>
              </div>
              {/* Intro body */}
              {introFetching ? (
                <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>{t.loading}</div>
              ) : (
                <div className="bible-intro-body">
                  {bookIntro && (
                    <div className="bible-intro-meta">
                      {[
                        [t.introFields.author,            bookIntro.author],
                        [t.introFields.timeOfWriting,     bookIntro.time_of_writing],
                        [t.introFields.placeOfWriting,    bookIntro.place_of_writing],
                        [t.introFields.timePeriodCovered, bookIntro.time_period_covered],
                        [t.introFields.recipient,         bookIntro.recipient],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} className="bible-intro-field">
                          <span className="bible-intro-label">{label}: </span>
                          <RefContent content={value} lang={introLang} contextBook={selectedBook} contextChapter={1} />
                        </div>
                      ))}
                    </div>
                  )}
                  {bookIntro?.subject && (
                    <div className="bible-intro-subject">
                      <div className="bible-intro-subject-label">{t.subjectOf(bookName(selectedBook, displayLang))}</div>
                      <div className="bible-intro-subject-text">{bookIntro.subject}</div>
                    </div>
                  )}
                  {bookOutline.length > 0 && (
                    <div className="bible-outline-section">
                      <div className="bible-outline-header">
                        <h3 className="bible-outline-title">{t.outline}</h3>
                        <button
                          className="bible-expand-all-btn"
                          onClick={() => {
                            if (outlineAllExpanded) {
                              setOutlineCollapseTrigger(n => n + 1);
                              setOutlineAllExpanded(false);
                            } else {
                              setOutlineExpandTrigger(n => n + 1);
                              setOutlineAllExpanded(true);
                            }
                          }}
                        >
                          {outlineAllExpanded ? t.collapseAll : t.expandAll}
                        </button>
                      </div>
                      <OutlineTree
                        nodes={outlineTree}
                        expandAllTrigger={outlineExpandTrigger}
                        collapseAllTrigger={outlineCollapseTrigger}
                        onNavigate={node => {
                          if (node.start_chapter) {
                            if (node.start_verse) pendingScrollVerse.current = node.start_verse;
                            selectChapter(node.start_chapter);
                          }
                        }}
                      />
                    </div>
                  )}
                  {!bookIntro && bookOutline.length === 0 && (
                    <p className="bible-intro-empty">Introduction data coming soon.</p>
                  )}
                </div>
              )}
            </div>
            </TurnPage>
            {/* The outline page is the first segment of its book's track. */}
            <ChapterScrubber book={selectedBook} chapter={null} lang={displayLang} t={t}
              onJump={jumpTo} onPreview={prefetchPage} />
            </>
          ) : (
            /* ── Chapter + verse view ── */
            selectedChapter && (
                <div
                  className="bible-verse-view"
                  ref={verseContentRef}
                >

                  {/* Sticky toolbar: chapter select + text/parallel/refs/outline + verse select */}
                  <div className="bible-sticky-toolbar">

                  {/* ── Chapter selection ── */}
                  <div className="bible-chapter-section">
                    <div className="bible-chapter-section-header">
                      <h2 className="bible-book-heading">{bookName(selectedBook, displayLang)}</h2>
                      <span className="bible-section-tag">{t.chapters}</span>
                    </div>
                    <div className="bible-chapter-grid">
                      {chapters.map(ch => (
                        <button
                          key={ch}
                          className={`bible-ch-btn${selectedChapter === ch ? ' active' : ''}`}
                          onClick={() => selectChapter(ch)}
                        >
                          {ch}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Controls row */}
                  <div className="bible-verse-header">
                    <div className="bible-verse-title-row">
                      {/* Phones get these two instead of a heading: back, and a
                          button that says where you are and opens the picker.
                          Both ride in this row so the back arrow does not cost
                          a line of its own. Desktop keeps the plain heading. */}
                      <button
                        className="bible-step-btn"
                        onClick={() => turnPage(-1)}
                        disabled={!turnPrev}
                        aria-label={t.prevChapter}
                      >
                        ‹
                      </button>
                      {/* Book, chapter, verse. Native selects on purpose: the
                          phone's own picker handles 66 books and Psalm 119's
                          176 verses better than any list we could draw, and it
                          is reachable by assistive tech for free. */}
                      <div className="bible-selects">
                        <select
                          className="bible-jump-select bible-jump-book"
                          value={selectedBook}
                          onChange={e => goTo({ book: e.target.value, ch: 1 })}
                          aria-label="Book"
                        >
                          <optgroup label={t.nt}>
                            {NT_BOOKS.map(abbr => (
                              <option key={abbr} value={abbr}>{bookName(abbr, displayLang)}</option>
                            ))}
                          </optgroup>
                          <optgroup label={t.ot}>
                            {OT_BOOKS.map(abbr => (
                              <option key={abbr} value={abbr}>{bookName(abbr, displayLang)}</option>
                            ))}
                          </optgroup>
                        </select>

                        <select
                          className="bible-jump-select bible-jump-ch"
                          value={selectedChapter}
                          onChange={e => goTo({ book: selectedBook, ch: Number(e.target.value) })}
                          aria-label={t.chapters}
                        >
                          {chapters.map(ch => <option key={ch} value={ch}>{ch}</option>)}
                        </select>

                        {/* A jump, not a position: it scrolls and then resets,
                            rather than pretending to track where you are. */}
                        <select
                          className="bible-jump-select bible-jump-vs"
                          value=""
                          onChange={e => {
                            const n = Number(e.target.value);
                            if (n) scrollToVerse(n);
                            e.target.value = '';
                          }}
                          aria-label={t.verse}
                        >
                          <option value="">{t.verse}</option>
                          {Array.from({ length: verseCount }, (_, i) => i + 1).map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>

                      <h3 className="bible-ch-heading">
                        {displayLang === 'zh' || displayLang === 'sc'
                          ? `第 ${selectedChapter} 章`
                          : `Chapter ${selectedChapter}`}
                      </h3>
                      <div className="bible-verse-controls" ref={viewMenuRef}>
                        {/* Sliders rather than dots: this adjusts how the
                            chapter reads, it is not a list of more actions. */}
                        <button
                          className={`bible-viewmenu-btn${viewMenuOpen ? ' active' : ''}`}
                          onClick={() => setViewMenuOpen(o => !o)}
                          aria-expanded={viewMenuOpen}
                          aria-label="Reading options"
                          title="Reading options"
                        >
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <line x1="4" y1="8" x2="20" y2="8" />
                            <circle cx="15" cy="8" r="2.4" fill="var(--bg)" />
                            <line x1="4" y1="16" x2="20" y2="16" />
                            <circle cx="9" cy="16" r="2.4" fill="var(--bg)" />
                          </svg>
                        </button>

                        <div className={`bible-view-menu${viewMenuOpen ? ' open' : ''}`}>
                          {/* Font size controls — same style as Reading page */}
                          <div className="font-size-row">
                            <span>{t.text}</span>
                            <button className="font-btn" onClick={() => setFontSize(f => Math.max(12, f - 2))}>A−</button>
                            <button className="font-btn" onClick={() => setFontSize(18)}>A</button>
                            <button className="font-btn" onClick={() => setFontSize(f => Math.min(26, f + 2))}>A+</button>
                          </div>

                          {/* Parallel toggle — same style as Reading page */}
                          <button
                            className={`parallel-btn${parallelMode ? ' active' : ''}`}
                            onClick={() => {
                              const next = !parallelMode;
                              setParallelMode(next);
                              if (next) setParallelLangA(displayLang);
                            }}
                          >
                            {parallelMode ? '✓ ' : ''}{t.parallel}
                          </button>

                          {/* Admin-only: references toggle */}
                          {canSeeRefs && (
                            <button
                              className={`parallel-btn${showRefs ? ' active' : ''}`}
                              title="Toggle cross-references and footnotes"
                              onClick={() => { setShowRefs(r => !r); closeAllPopups(); }}
                            >
                              {showRefs ? '✓ ' : ''}📖 Refs
                            </button>
                          )}
                          {/* Outline toggle — only shown when chapter has outline data */}
                          {chapterOutlines.length > 0 && (
                            <button
                              className={`parallel-btn${showOutline ? ' active' : ''}`}
                              title="Toggle outline headers in reading"
                              onClick={() => setShowOutline(v => !v)}
                            >
                              {showOutline ? '✓ ' : ''}§ Outline
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Forward sits at the far end of the bar, so the two
                          arrows bracket everything between them. */}
                      <button
                        className="bible-step-btn bible-step-next"
                        onClick={() => turnPage(1)}
                        disabled={!turnNext}
                        aria-label={t.nextChapter}
                      >
                        ›
                      </button>
                    </div>

                    {/* Parallel language bar — same layout as Reading page */}
                    {parallelMode && (
                      <div className="parallel-lang-bar">
                        <select className="parallel-lang-select" value={parallelLangA} onChange={e => setParallelLangA(e.target.value)}>
                          {LANG_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                        </select>
                        <span className="parallel-sep">⇔</span>
                        <select className="parallel-lang-select" value={parallelLangB} onChange={e => setParallelLangB(e.target.value)}>
                          {LANG_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Verse jump strip */}
                  {!chapterLoading && verseCount > 0 && (
                    <div className="bible-verse-jump">
                      <span className="bible-verse-jump-label">{t.verse}</span>
                      <div className="bible-verse-jump-strip">
                        {verseJumpStrip}
                      </div>
                    </div>
                  )}

                  </div>{/* end .bible-sticky-toolbar */}

                  {/* The page that turns: the recording and the text. The bars
                      above stay put, like the spine of the book. */}
                  <TurnPage turner={turner}>
                  {/* Audio */}
                  {chapterData && (getAudioSrc(parallelMode ? parallelLangA : displayLang) || (parallelMode && getAudioSrc(parallelLangB))) && (
                    <div className={parallelMode ? 'parallel-audio-row' : 'bible-audio-single'}>
                      {getAudioSrc(parallelMode ? parallelLangA : displayLang)
                        ? <audio ref={audioElA} key={`a-${selectedBook}-${parallelMode ? parallelLangA : displayLang}-${selectedChapter}`} controls src={getAudioSrc(parallelMode ? parallelLangA : displayLang)} style={{ width: '100%' }} {...audioEvents('a', rowA)} />
                        : parallelMode ? <div /> : null}
                      {parallelMode && (
                        getAudioSrc(parallelLangB)
                          ? <audio ref={audioElB} key={`b-${selectedBook}-${parallelLangB}-${selectedChapter}`} controls src={getAudioSrc(parallelLangB)} style={{ width: '100%' }} {...audioEvents('b', rowB)} />
                          : <div />
                      )}
                    </div>
                  )}

                  {/* Verse content */}
                  {chapterLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>{t.loading}</div>
                  ) : (
                    <div ref={annoRootRef} className={`bible-verse-box bible-text-${parallelMode ? parallelLangA : displayLang}`} onClick={closeAllPopups}>
                      {parallelMode ? (
                        <>
                          <div className="parallel-titles-row" style={{ fontWeight: 700, opacity: 0.6, marginBottom: '10px', fontSize: '13px' }}>
                            <div>{langLabel(parallelLangA)}</div>
                            <div>{langLabel(parallelLangB)}</div>
                          </div>
                          {renderedVersesParallel}
                        </>
                      ) : (
                        renderedVerses
                      )}
                    </div>
                  )}
                  </TurnPage>

                  <ChapterScrubber book={selectedBook} chapter={selectedChapter} lang={displayLang} t={t}
                    onJump={jumpTo} onPreview={prefetchPage} />
                </div>
              )
          )
        )}
      </div>
    </div>
    )}
    </>
    </PopupContext.Provider>
  );
}
